import React, { FC, MouseEventHandler, ReactElement } from 'react';
import { RouterLocation } from '@funtech-int/rival';
import { SelectionMovie, ViewOption } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { useSignal } from '@funtech-int/rival-react';
import { createPictureFactory, Picture } from '@funtech-int/yango-shared';

import { useProfileManager } from '@/shared/hooks/useProfileManager';

import * as CardKit from '../CardKit';
import { useI18n } from '../../hooks/useI18n';
import { useAnnounceI18n } from '../../hooks/useAnnouncePromiseI18n';
import { useInMemoryCache } from '../../hooks/useInMemoryCache';
import { getMovieUGCKey } from '../../lib/movieUGC';

import styles from './_Movie.module.css';

const pictureFactory = createPictureFactory({
    fallback: {
        srcSet: {
            '1x': '375x234',
            '2x': '640x360', // actually 1.7x but this is maximum available size
        },
        src: '375x234',
    },
});

interface MovieProps {
    movie: SelectionMovie;
    location: RouterLocation<any, any>;
    withPlannedToWatch?: boolean;
    watchPeriod?: ViewOption['watchPeriod'];
    onShow?: VoidFunction;
    onClick?: MouseEventHandler<HTMLAnchorElement>;
}

export const Movie: FC<MovieProps> = ({
    movie,
    location,
    withPlannedToWatch,
    watchPeriod,
    onShow,
    onClick,
}) => {
    const i18n = useI18n();

    const { img } = pictureFactory({
        fallbackUrl: movie.horizontalPoster?.toString(),
    });

    const hasSubscription = useSignal(useProfileManager().hasSubscription);

    const plannedToWatchSignal = useInMemoryCache().getSignal(
        getMovieUGCKey(movie.id, 'plannedToWatch'),
        Boolean(movie.userData?.isPlannedToWatch)
    );

    const isPlannedToWatch = useSignal(plannedToWatchSignal);

    const announceI18n = useAnnounceI18n();

    let lock: ReactElement | undefined;
    let announceSlot: ReactElement | undefined;
    let plannedToWatchBadge: ReactElement | undefined;
    let purchaseBadge: ReactElement | undefined;
    let top10Badge: ReactElement | undefined;
    let watchPeriodBadge: ReactElement | undefined;

    if (!movie.viewOption || (!movie.viewOption.isWatchable && !movie.availabilityAnounce)) {
        lock = <CardKit.Lock />;
    }

    if (movie.availabilityAnounce) {
        const { availabilityAnounce } = movie;

        announceSlot = (
            <CardKit.Announce
                text={announceI18n.getPromise(availabilityAnounce.announcePromise)}
                availabilityDate={availabilityAnounce.availabilityDate}
                footerText={
                    availabilityAnounce.isSeasonAnnounce ? announceI18n.getSeason() : undefined
                }
            />
        );
    }

    if (withPlannedToWatch && isPlannedToWatch) {
        plannedToWatchBadge = <CardKit.FavoriteBadge ariaLabel={i18n('card__planned_to_watch')} />;
    }

    if (hasSubscription && movie.viewOption?.isPurchasable && movie.viewOption.subscriptionBadge) {
        purchaseBadge = (
            <CardKit.PurchaseBadge
                ariaLabel={i18n('card__purchasable')}
                badge={movie.viewOption.subscriptionBadge}
            />
        );
    }

    if (movie.top10) {
        top10Badge = <CardKit.TopTenBadge title={i18n('card__top10')} />;
    }

    if (watchPeriod) {
        const text =
            watchPeriod.watchPeriodStatus === 'EXPIRED' ? i18n('card__expired') : undefined;

        watchPeriodBadge = text ? (
            <CardKit.WaitingBadge text={text} viewVariant="default" />
        ) : undefined;
    }

    return (
        <CardKit.Layout
            location={location}
            onClick={onClick}
            onShow={onShow}
            withShadow
            dataTid="CardMovie"
        >
            <Picture
                className={styles.image}
                alt={movie.title.toString()}
                loading="lazy"
                srcSet={img.srcSet}
                src={img.src}
                data-tid="AdaptiveImage"
            />
            {announceSlot}
            <div className={styles.badges}>
                {plannedToWatchBadge}
                {purchaseBadge}
                {top10Badge}
                {watchPeriodBadge}
            </div>
            {lock}
        </CardKit.Layout>
    );
};
